/** Camera +Z is backward; the selected avatars face local +Z. Keep these
 * conventions explicit when combining tracked head look, controls and bodies. */
const finiteQuaternion = q => q && ['x', 'y', 'z', 'w'].every(k => Number.isFinite(q[k])) && Math.hypot(q.x, q.y, q.z, q.w) > 1e-8;
const wrap = angle => Math.atan2(Math.sin(angle), Math.cos(angle));
export function cameraHeadingYaw(quaternion, fallback = null) {
  if (!finiteQuaternion(quaternion)) return fallback;
  const {x, y, z, w} = quaternion, norm = x * x + y * y + z * z + w * w;
  const backwardX = 2 * (x * z + w * y) / norm, backwardZ = 1 - 2 * (x * x + y * y) / norm;
  // Looking straight up/down must not flip the body on numerical noise.
  return Math.hypot(backwardX, backwardZ) > 1e-4 ? Math.atan2(backwardX, backwardZ) : fallback;
}

export function headRelativeMovement({x = 0, z = 0, cameraYaw = 0, headYaw = null, fallbackYaw = 0} = {}) {
  const length = Math.hypot(x, z);
  if (length > 1) { x /= length; z /= length; }
  const lookYaw = cameraYaw + (Number.isFinite(headYaw) ? headYaw : 0);
  const moveX = x * Math.cos(lookYaw) + z * Math.sin(lookYaw), moveZ = -x * Math.sin(lookYaw) + z * Math.cos(lookYaw);
  return {moveX, moveZ, lookYaw, yaw: length ? Math.atan2(moveX, moveZ) : fallbackYaw};
}

/** Velocity and controls use the player's simulation chart. The rendered
 * native camera adds any portal chart rotation; tracked look is composed only
 * for idle facing. Source rider orientation is deliberately outside this path. */
export function firstPersonBodyHeading({player, cameraQuaternion, viewQuaternion, controlYaw = 0} = {}) {
  const baseYaw = cameraHeadingYaw(cameraQuaternion, controlYaw);
  const vx = player?.velocityX ?? 0, vz = player?.velocityZ ?? 0;
  if (Number.isFinite(vx) && Number.isFinite(vz) && Math.hypot(vx, vz) > .15) return wrap(Math.atan2(vx, vz) + baseYaw - controlYaw);
  return wrap(cameraHeadingYaw(viewQuaternion, baseYaw) + Math.PI);
}

/** A synchronous correction of only the selected local body's rendered yaw.
 * Rotate around the untracked optical anchor, so turning cannot feed back into
 * next frame's eye calibration or drag the body along with physical head lean.
 * Animator/root properties and source-owned matrices are restored exactly.
 */
export function createFirstPersonHeading({THREE: T}) {
  const buffers = new WeakMap();
  const validMatrix = m => m?.elements?.every(Number.isFinite) && Math.abs(m.determinant()) > 1e-15;
  function statesFor(body) {
    let states = buffers.get(body);
    const nodes = []; body.traverse(node => nodes.push(node));
    if (!states || states.active || states.nodes.length !== nodes.length || states.nodes.some((state, i) => state.node !== nodes[i])) {
      const skeletons = new Set(nodes.flatMap(node => node.skeleton ? [node.skeleton] : []));
      states = {active: false, nodes: nodes.map(node => ({node, matrix: new T.Matrix4(), world: new T.Matrix4(), bind: node.isSkinnedMesh ? new T.Matrix4() : null})), skeletons: [...skeletons].map(skeleton => ({skeleton, matrices: null}))};
      if (!buffers.get(body)?.active) buffers.set(body, states);
    }
    return states;
  }
  return {
    withFrame({avatarRoot, avatarBody = avatarRoot, yaw, anchorPosition, enabled = true} = {}, draw) {
      if (buffers.get(avatarBody)?.active) return draw();
      if (!enabled || !Number.isFinite(yaw) || !avatarRoot?.isObject3D || !avatarBody?.isObject3D || !avatarBody.parent || !anchorPosition || !['x', 'y', 'z'].every(k => Number.isFinite(anchorPosition[k])) || !validMatrix(avatarRoot.matrixWorld) || !validMatrix(avatarBody.matrixWorld) || !validMatrix(avatarBody.parent.matrixWorld)) return draw();
      const e = avatarRoot.matrixWorld.elements, nativeYaw = Math.atan2(e[8], e[10]);
      if (Math.hypot(e[8], e[10]) < 1e-8) return draw();
      const angle = wrap(yaw - nativeYaw);
      if (Math.abs(angle) < 1e-8) return draw();
      const states = statesFor(avatarBody); states.active = true;
      for (const s of states.nodes) {
        s.matrix.copy(s.node.matrix); s.world.copy(s.node.matrixWorld); s.auto = s.node.matrixAutoUpdate; s.worldAuto = s.node.matrixWorldAutoUpdate; s.dirty = s.node.matrixWorldNeedsUpdate;
        if (s.bind) s.bind.copy(s.node.bindMatrixInverse);
      }
      for (const s of states.skeletons) {
        if (s.matrices?.length === s.skeleton.boneMatrices?.length) s.matrices?.set(s.skeleton.boneMatrices);
        else s.matrices = s.skeleton.boneMatrices?.slice();
      }
      try {
        const p = anchorPosition, delta = new T.Matrix4().makeTranslation(p.x, p.y, p.z).multiply(new T.Matrix4().makeRotationY(angle)).multiply(new T.Matrix4().makeTranslation(-p.x, -p.y, -p.z));
        // Updating world storage directly also handles SSX-owned frozen bones.
        // Only the body's local matrix changes; every authored descendant local
        // matrix, local quaternion and animation channel remains untouched.
        for (const s of states.nodes) { s.node.matrixWorld.multiplyMatrices(delta, s.world); s.node.matrixWorldNeedsUpdate = false; }
        avatarBody.matrix.copy(avatarBody.parent.matrixWorld).invert().multiply(avatarBody.matrixWorld);
        avatarBody.matrixAutoUpdate = false;
        for (const s of states.skeletons) s.skeleton.update();
        return draw();
      } finally {
        for (const s of states.nodes) {
          s.node.matrix.copy(s.matrix); s.node.matrixWorld.copy(s.world); s.node.matrixAutoUpdate = s.auto; s.node.matrixWorldAutoUpdate = s.worldAuto; s.node.matrixWorldNeedsUpdate = s.dirty;
          if (s.bind) s.node.bindMatrixInverse.copy(s.bind);
        }
        for (const {skeleton, matrices} of states.skeletons) if (matrices) { skeleton.boneMatrices.set(matrices); if (skeleton.boneTexture) skeleton.boneTexture.needsUpdate = true; }
        states.active = false;
      }
    },
  };
}
